import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import useProviderConfig from 'hooks/useProviderConfig';
import { TableCell, TableRow } from './ui/table';
import { getProviderDisplayName } from './lib/ordering';

const sourceValueOf = (sources, source) =>
  sources.find((item) => item.source === source)?.value ?? 0;

// Daily per-source lists keyed by date, read through `get(date, field)`.
// `field` names the list on each daily entry (`cardBySource` etc. for the
// payment methods).
export const indexDailySources = (daily) => {
  const byDate = new Map((daily || []).map((day) => [day.date, day]));
  return (date, field) => byDate.get(date)?.[field] || [];
};

// Logo and display name for an order source, the same way order details shows
// a provider.
export function SourceLabel({ source, compact = false }) {
  const { providers } = useProviderConfig();
  const logoUrl = providers[source]?.logoUrl;
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap ${
        compact ? 'gap-1.5' : 'gap-2'
      }`}
    >
      {logoUrl && (
        <img
          src={logoUrl}
          alt=""
          className={`rounded-sm object-contain ${
            compact ? 'h-4 w-4' : 'h-5 w-5'
          }`}
        />
      )}
      {getProviderDisplayName(source)}
    </span>
  );
}

// Adds several per-source lists into one, keeping first-seen source order.
export const sumSources = (lists) => {
  const totals = new Map();
  lists.forEach((list) =>
    (list || []).forEach(({ source, value }) =>
      totals.set(source, (totals.get(source) || 0) + (value || 0))
    )
  );
  return [...totals].map(([source, value]) => ({ source, value }));
};

// A table row whose label expands into one sub-row per order source. `days`
// holds the row's own value per date column; the sub-rows read theirs through
// `getDaySources(date)`.
export function SourceRow({
  label,
  total,
  days,
  sources,
  getDaySources,
  format,
  rowClassName,
  labelClassName = 'font-medium',
  totalClassName = 'text-right font-bold',
  dayClassName = 'text-right',
}) {
  const [open, setOpen] = useState(false);
  const hasSources = Boolean(sources?.length);

  return (
    <>
      <TableRow className={rowClassName}>
        <TableCell className={`py-2 whitespace-nowrap ${labelClassName}`}>
          {hasSources ? (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 text-left"
            >
              <ChevronRight
                className={`h-4 w-4 shrink-0 transition-transform print:hidden ${
                  open ? 'rotate-90' : ''
                }`}
              />
              {label}
            </button>
          ) : (
            label
          )}
        </TableCell>
        <TableCell className={`py-2 ${totalClassName}`}>
          {format(total)}
        </TableCell>
        {days.map((day) => (
          <TableCell key={day.date} className={`py-2 ${dayClassName}`}>
            {format(day.value)}
          </TableCell>
        ))}
      </TableRow>
      {open &&
        sources.map(({ source, value }) => (
          <TableRow
            key={source}
            className="bg-muted/40"
            style={{ fontStyle: 'italic' }}
          >
            <TableCell className="py-1 pl-10 text-[13px] whitespace-nowrap">
              <SourceLabel source={source} compact />
            </TableCell>
            <TableCell className="py-1 text-right text-[13px]">
              {format(value)}
            </TableCell>
            {days.map((day) => (
              <TableCell key={day.date} className="py-1 text-right text-[13px]">
                {format(sourceValueOf(getDaySources(day.date), source))}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </>
  );
}

// Per-source list shown under a summary card's value.
export function SourceBreakdownList({ sources, format }) {
  if (!sources?.length) return null;

  return (
    <ul className="mt-2 space-y-1">
      {sources.map(({ source, value }) => (
        <li
          key={source}
          className="flex items-center justify-between gap-4 text-sm"
        >
          <SourceLabel source={source} />
          <span className="font-medium">{format(value)}</span>
        </li>
      ))}
    </ul>
  );
}
